import { SECTION_TITLE_DATE_FORMAT } from "@miplatform/constants";
import {
  addWorkDays,
  formatDate,
  getDate,
  getRequester,
  isDateAfter,
  isSameOrAfter,
  setTime,
} from "@miplatform/utils";
import { ORDERS_TABS_NAMES } from "../ordersConstants";
import { AllOrderActions } from "./AllOrderActions/AllOrderActions";
import { ApprovedOrderActions } from "./ApprovedOrderActions/ApprovedOrderActions";
import { AssignedOrderActions } from "./AssignedOrderActions/AssignedOrderActions";
import { CancelledOrderActions } from "./CancelledOrderActions/CancelledOrderActions";
import { CompletedOrderActions } from "./CompletedOrderActions/CompletedOrderActions";
import { ConfirmedOrderActions } from "./ConfirmedOrderActions/ConfirmedOrderActions";
import { HoldOrderActions } from "./HoldOrders/HoldOrderActions";
import { HoldOrderListItems } from "./HoldOrders/HoldOrderListItems";
import { NewOrderActions } from "./NewOrderActions/NewOrderActions";
import { ScheduledOrderActions } from "./ScheduledOrderActions/ScheduledOrderActions";

/**
 * Configuration for different order list views based on tab selection.
 * Each configuration contains methods and components specific to the tab.
 */
export const ORDERS_LIST_CONFIG = {
  [ORDERS_TABS_NAMES.NEW]: {
    getGroupedTitle: (title) => `Submitted Date: ${title}`,
    getGroupedCriteria: (item) =>
      formatDate(item.receivedDate, SECTION_TITLE_DATE_FORMAT),
    ActionsComponent: NewOrderActions,
  },
  [ORDERS_TABS_NAMES.RESCHEDULE]: {
    getGroupedTitle: (title) => `Requested Start Date: ${title}`,
    getGroupedCriteria: (item) =>
      formatDate(item.requestedStartDate, SECTION_TITLE_DATE_FORMAT),
    isAlert: (item) => !!item.rescheduleReason?.color,
    alertColor: (item) => item.rescheduleReason?.color,
    ActionsComponent: NewOrderActions,
  },
  [ORDERS_TABS_NAMES.APPROVED]: {
    getGroupedTitle: (title) => `Approved on: ${title}`,
    getGroupedCriteria: (item) =>
      formatDate(item.approvedStartDate, SECTION_TITLE_DATE_FORMAT),
    ActionsComponent: ApprovedOrderActions,
  },
  [ORDERS_TABS_NAMES.SCHEDULED]: {
    getGroupedTitle: (item) => item || "No Requestor",
    getGroupedCriteria: (item) => getRequester(item?.requester),
    isAlert: (item) => !!getScheduledAlert(item),
    getAlerts: (item) => getScheduledAlert(item),
    ActionsComponent: ScheduledOrderActions,
  },
  [ORDERS_TABS_NAMES.CANCELLED]: {
    getGroupedTitle: (item) => item || "No Requestor",
    getGroupedCriteria: (item) => getRequester(item?.requester),
    ActionsComponent: CancelledOrderActions,
  },
  [ORDERS_TABS_NAMES.COMPLETE]: {
    getGroupedTitle: (title) => `Completion Start Date: ${title}`,
    getGroupedCriteria: (item) =>
      formatDate(item.completeDate, SECTION_TITLE_DATE_FORMAT),
    ActionsComponent: CompletedOrderActions,
  },
  [ORDERS_TABS_NAMES.CONFIRMED]: {
    isAlert: (item) => !!getInProgressAlert(item),
    getAlerts: (item) => getInProgressAlert(item),
    isLate: (item) => !!item.pushedToDate,
    getGroupedTitle: (item) => item || "No Requestor",
    getGroupedCriteria: (item) => getRequester(item?.requester),
    ActionsComponent: ConfirmedOrderActions,
  },
  [ORDERS_TABS_NAMES.ASSIGNED]: {
    getGroupedTitle: (title) => `Assigned Date: ${title}`,
    getGroupedCriteria: (item) =>
      formatDate(item.assignedDate, SECTION_TITLE_DATE_FORMAT),
    ActionsComponent: AssignedOrderActions,
  },
  [ORDERS_TABS_NAMES.HOLDS]: {
    getGroupedTitle: (title) => `Follow up on: ${title}`,
    getGroupedCriteria: (item) =>
      formatDate(item.followUpDate, SECTION_TITLE_DATE_FORMAT),
    ActionsComponent: HoldOrderActions,
    sections: [
      {
        fieldsetStyle: {
          justifyContent: "flex-end",
          alignItems: "center",
        },
        fieldset: [
          {
            field: "priority",
            maxWidth: 400,
            renderCell: (order) => {
              return <HoldOrderListItems {...order} />;
            },
          },
        ],
      },
    ],
  },
  [ORDERS_TABS_NAMES.FOLLOW_UP]: {
    getGroupedTitle: (title) => `Follow up on: ${title}`,
    getGroupedCriteria: (item) =>
      formatDate(item.followUpDate, SECTION_TITLE_DATE_FORMAT),
    ActionsComponent: HoldOrderActions,
    sections: [
      {
        fieldsetStyle: {
          justifyContent: "flex-end",
          alignItems: "center",
        },
        fieldset: [
          {
            field: "priority",
            maxWidth: 400,
            renderCell: (order) => {
              return <HoldOrderListItems {...order} />;
            },
          },
        ],
      },
    ],
  },
  [ORDERS_TABS_NAMES.ALL]: {
    ActionsComponent: AllOrderActions,
  },
};

const getScheduledAlert = (item) => {
  if (!item.orderTemplateConfirmationLeadTime) {
    return null;
  }

  const cutOffDate = addWorkDays(item.orderTemplateConfirmationLeadDays);
  const approvedStartDate = setTime(
    item.approvedStartDate,
    item.orderTemplateConfirmationLeadTime
  );

  const isAlert = isSameOrAfter(cutOffDate, approvedStartDate, "minute");
  if (!isAlert) {
    return null;
  }
  return "The order confirmation is late";
};

const getInProgressAlert = (item) => {
  if (
    !item.inProgressStartedDate &&
    isDateAfter(getDate(), item.approvedStartDate)
  ) {
    return "The approved start date is past and field work has not started yet";
  }

  return null;
};
